import { useEffect, useId, useRef, useState } from "react";
import type { AgentDTO, SkillDetailDTO, SkillLevel, SubjectDTO, UserDTO } from "../../shared/types";
import { useUser } from "../App";
import { get, post } from "../lib/api";
import { useScopeTypes } from "../lib/scopes";
import { Icon } from "./Icon";
import { MultiSelect } from "./MultiSelect";
import { ScopePicker } from "./ScopePicker";
import { agentsForPersonSkills, LEVEL_ICON } from "./SkillWho";
import { Button, Choice, ErrorText, Field, FieldGroup, inputCls } from "./ui";

export type SkillFileInput = { path: string; text?: string; base64?: string; executable?: boolean };

const NAME = /^[a-z0-9][a-z0-9-]{0,62}$/;
const NEVER = "Never put a key or a password in a skill: a run is handed every file, and so is whoever owns its chat.";

/** Where a new skill applies: one person's (shared with agents), one scope's, or the agents' it is attached to. */
type Place = { level: SkillLevel; userId: string; subject: SubjectDTO | null; allAgents: boolean; agentIds: string[] };

/** From a folder's name: a finished slug. */
const slugify = (value: string) => value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 63);

/** While typing: only the characters a name may not hold are replaced, so a half-written name survives a keystroke. */
const typed = (value: string) => value.toLowerCase().replace(/[^a-z0-9-]+/g, "-").slice(0, 63);

/** A place that is half chosen would land the skill somewhere wider than the person picked, or on no agent at all. */
const placeReady = (place: Place) =>
  place.level === "scope"
    ? Boolean(place.subject)
    : place.level === "person"
      ? Boolean(place.userId) && place.agentIds.length > 0
      : place.allAgents || place.agentIds.length > 0;

/** UTF-8 when it decodes as UTF-8, bytes otherwise; built in chunks, because a whole file breaks the argument limit. */
export async function fileToInput(file: File, path: string): Promise<SkillFileInput> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  try {
    return { path, text: new TextDecoder("utf-8", { fatal: true, ignoreBOM: true }).decode(bytes) };
  } catch {
    let binary = "";
    for (let i = 0; i < bytes.length; i += 8192) binary += String.fromCharCode(...bytes.subarray(i, i + 8192));
    return { path, base64: btoa(binary) };
  }
}

const placeBody = (place: Place) =>
  place.level === "scope"
    ? { subjectId: place.subject?.id }
    : {
        ...(place.level === "person" ? { userId: place.userId } : {}),
        allAgents: place.allAgents,
        agentIds: place.allAgents ? [] : place.agentIds,
      };

/** "a, b and c": how a hint names the agents it is about. */
export const names = (list: { name: string }[]) =>
  list.length <= 1 ? (list[0]?.name ?? "") : `${list.slice(0, -1).map((a) => a.name).join(", ")} and ${list.at(-1)!.name}`;

/**
 * An agents' own skill: every agent, now and every one made later, or only the ones chosen. Only the agents the
 * person choosing answers for are offered (the ones they own, and the ones nobody owns), plus the ones it already
 * has: an agent's owners decide what it follows, and the platform refuses anybody else. `locked` says why the
 * choice is not theirs to change.
 */
export function AgentChoice({ agents, every, chosen, onEvery, onChosen, locked, wide = false }: {
  agents: AgentDTO[];
  every: boolean;
  chosen: string[];
  onEvery: (every: boolean) => void;
  onChosen: (agentIds: string[]) => void;
  locked?: string;
  wide?: boolean;
}) {
  const group = useId();
  const offered = agents.filter((a) => (a.youAnswerFor && !a.archivedAt) || chosen.includes(a.id));
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" name={group} checked={every} disabled={Boolean(locked)} onChange={() => onEvery(true)} />
        Every agent <span className="text-faint">— now and every one made later</span>
      </label>
      <label className="flex items-center gap-2 text-sm">
        <input type="radio" name={group} checked={!every} disabled={Boolean(locked)} onChange={() => onEvery(false)} />
        Only the agents I choose
      </label>
      {locked ? (
        <p className="text-xs text-muted">{locked}</p>
      ) : (
        !every && (
          <MultiSelect
            wide={wide}
            options={offered.map((a) => ({ value: a.id, label: a.name, hint: a.slug }))}
            selected={chosen}
            onChange={onChosen}
            placeholder={offered.length ? "Choose agents" : "No agent is yours to choose"}
          />
        )
      )}
    </div>
  );
}

/** Which agents may have a person's own skills, in words: the ones that take everyone's, and the ones they own. */
export function personAgentsHint(agents: AgentDTO[], userId: string, you: boolean, name: string): string {
  const allowed = agentsForPersonSkills(agents, userId);
  if (!allowed.length) {
    return `No agent can have ${you ? "your" : `${name}'s`} own skills yet: switch on "People can add their own skills" in an agent's settings, or make ${you ? "yourself" : "them"} an owner of one.`;
  }
  const takers = allowed.filter((a) => a.peopleCanAddSkills);
  const owned = allowed.filter((a) => !a.peopleCanAddSkills);
  const parts = [
    ...(takers.length ? [`${names(takers)} ${takers.length === 1 ? "takes" : "take"} everyone's own skills`] : []),
    ...(owned.length ? [`${you ? "you own" : `${name} owns`} ${names(owned)}`] : []),
  ];
  const said = parts.join(", and ");
  return `${said.charAt(0).toUpperCase()}${said.slice(1)}. No other agent can have them.`;
}

/** The places a skill can apply, in the order people pick them, each with what choosing it means. */
const PLACES: { level: SkillLevel; label: string; means: string }[] = [
  { level: "agent", label: "Agent", means: "The agents you choose follow it, whoever they are working for." },
  { level: "scope", label: "Scope", means: "Every agent working under one scope follows it." },
  { level: "person", label: "Person", means: "Agents follow it only when they are working for that one person." },
];

/** Where a skill applies (agents, a scope or one person), and under it only the choices that place has. */
function PlaceFields({ agents, place, onChange }: { agents: AgentDTO[]; place: Place; onChange: (place: Place) => void }) {
  const me = useUser();
  const { types } = useScopeTypes();
  const [people, setPeople] = useState<UserDTO[]>([]);
  useEffect(() => {
    if (place.level === "person" && people.length === 0) get<UserDTO[]>("/api/users").then(setPeople, () => {});
  }, [place.level]);
  // A person's skill starts on every agent that may have it, which is usually just Pandito: the platform refuses
  // any other, so nobody is offered one.
  const forPerson = (userId: string) => agentsForPersonSkills(agents, userId).map((a) => a.id);
  const choose = (level: SkillLevel) => {
    if (level === place.level) return;
    const userId = place.userId || me.id;
    onChange({ ...place, level, userId, allAgents: level === "agent", agentIds: level === "person" ? forPerson(userId) : [] });
  };
  const you = place.userId === me.id;
  const name = people.find((u) => u.id === place.userId)?.name.split(" ")[0] ?? "this person";
  const answered = agents.filter((a) => a.youAnswerFor && !a.archivedAt);
  return (
    <div className="space-y-4">
      <FieldGroup label="Applies to" hint={PLACES.find((p) => p.level === place.level)!.means}>
        <Choice
          label="Applies to"
          value={place.level}
          onChange={choose}
          options={PLACES.map((p) => ({ value: p.level, label: <><Icon name={LEVEL_ICON[p.level]} className="size-3.5" />{p.label}</> }))}
        />
      </FieldGroup>
      {place.level === "agent" && (
        <FieldGroup
          label="Which agents"
          hint={
            answered.length
              ? "You can choose the agents you own and the ones nobody owns: an agent's owners decide what it follows."
              : "No agent is yours to choose: an agent's owners decide what it follows. Pick every agent, or ask its owners."
          }
        >
          <AgentChoice
            wide
            agents={agents}
            every={place.allAgents}
            chosen={place.agentIds}
            onEvery={(every) => onChange({ ...place, allAgents: every, agentIds: every ? [] : place.agentIds })}
            onChosen={(agentIds) => onChange({ ...place, agentIds })}
          />
        </FieldGroup>
      )}
      {place.level === "scope" && (
        <FieldGroup label="Which scope" hint={types?.length === 0 ? "No scope types yet: make one on the Scopes page." : "Search by name or id, across every type."}>
          <ScopePicker types={types ?? []} value={place.subject} onChange={(subject) => onChange({ ...place, subject })} />
        </FieldGroup>
      )}
      {place.level === "person" && (
        <>
          <Field label="Which person" hint="Only runs working for them are ever given it. Writing one for somebody else is recorded.">
            <select className={inputCls} value={place.userId} onChange={(e) => onChange({ ...place, userId: e.target.value, agentIds: forPerson(e.target.value) })}>
              <option value={me.id}>Me — {me.name}</option>
              {people.filter((u) => u.kind === "person" && u.id !== me.id).map((u) => (
                <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
              ))}
            </select>
          </Field>
          <FieldGroup label={you ? "Which of your agents" : `Which of ${name}'s agents`} hint={personAgentsHint(agents, place.userId, you, name)}>
            <MultiSelect
              wide
              all={you ? "All your agents" : `All of ${name}'s agents`}
              options={agentsForPersonSkills(agents, place.userId).map((a) => ({ value: a.id, label: a.name, hint: a.slug }))}
              selected={place.agentIds}
              onChange={(agentIds) => onChange({ ...place, agentIds })}
              placeholder="Choose agents"
            />
          </FieldGroup>
        </>
      )}
    </div>
  );
}

/** Where a new skill goes first: on the scope it is made from, else given to every agent. */
const firstPlace = (scope?: SubjectDTO): Place => ({ level: scope ? "scope" : "agent", userId: "", subject: scope ?? null, allAgents: true, agentIds: [] });

/** Write a skill here: the page composes SKILL.md from the name, the description and the body. */
export function NewSkillForm({ agents, scope, onCreated }: { agents: AgentDTO[]; scope?: SubjectDTO; onCreated: (skill: SkillDetailDTO) => void }) {
  const [form, setForm] = useState({ name: "", description: "", body: "" });
  const [place, setPlace] = useState<Place>(() => firstPlace(scope));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ready = NAME.test(form.name) && form.description.trim().length > 0 && form.body.trim().length > 0 && placeReady(place);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Name" hint="Lowercase letters, digits and dashes. Agents load it by this name.">
          <input className={`${inputCls} font-mono`} value={form.name} placeholder="monthly-tradein-report" onChange={(e) => setForm({ ...form, name: typed(e.target.value) })} />
        </Field>
        <Field label="Description" hint="The one line every run of the agent is shown. Say when to use the skill.">
          <input className={inputCls} value={form.description} placeholder="Export and summarise the monthly trade-in report for an account" onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
      </div>
      <Field label="Instructions" hint="The procedure itself, in markdown. Point at another skill with [[its-name]].">
        <textarea className={`${inputCls} min-h-48 font-mono text-xs`} value={form.body} placeholder="1. Pull the account's devices …" onChange={(e) => setForm({ ...form, body: e.target.value })} />
      </Field>
      <PlaceFields agents={agents} place={place} onChange={setPlace} />
      <p className="text-xs text-muted">{NEVER}</p>
      <ErrorText error={error} />
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={busy || !ready}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              const text = `---\nname: ${form.name}\ndescription: ${form.description.trim()}\n---\n\n${form.body.trim()}\n`;
              onCreated(await post<SkillDetailDTO>("/api/skills", { name: form.name, ...placeBody(place), files: [{ path: "SKILL.md", text }] }));
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Creating…" : "Create skill"}
        </Button>
      </div>
    </div>
  );
}

/** Import a folder from disk: SKILL.md plus its scripts, templates and reference files, as they are. */
export function ImportSkillForm({ agents, onCreated }: { agents: AgentDTO[]; onCreated: (skill: SkillDetailDTO) => void }) {
  const picker = useRef<HTMLInputElement>(null);
  const [name, setName] = useState("");
  const [files, setFiles] = useState<SkillFileInput[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [place, setPlace] = useState<Place>(() => firstPlace());
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pick = async (picked: FileList | null) => {
    if (!picked?.length) return;
    setError(null);
    const left: string[] = [];
    const taken: SkillFileInput[] = [];
    const chosen = Array.from(picked);
    const paths = chosen.map((file) => file.webkitRelativePath || file.name);
    // A folder pick names every file under its top folder; strip that folder only when every file shares it.
    const first = paths[0]!.split("/");
    const top = first.length > 1 && paths.every((path) => path.split("/")[0] === first[0]) ? first[0]! : null;
    for (const [i, file] of chosen.entries()) {
      const parts = paths[i]!.split("/");
      const inSkill = top ? parts.slice(1) : parts;
      const path = inSkill.join("/");
      // A dotfile or a dot-folder is machinery (.DS_Store, .git/), never part of the procedure.
      if (!path || inSkill.some((part) => part.startsWith("."))) left.push(paths[i]!);
      else taken.push(await fileToInput(file, path));
    }
    setName(slugify(top ?? first[0]!.replace(/\.[^.]+$/, "")));
    setFiles(taken);
    setSkipped(left);
  };

  return (
    <div className="space-y-4">
      <input
        ref={picker}
        type="file"
        multiple
        hidden
        {...({ webkitdirectory: "", directory: "" } as Record<string, string>)}
        onChange={(e) => {
          void pick(e.target.files);
          e.target.value = "";
        }}
      />
      <Field label="The folder" hint="A folder with SKILL.md at its top, plus any scripts, templates and reference files.">
        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => picker.current?.click()}>{files.length ? "Choose another folder" : "Choose a folder…"}</Button>
          {files.length > 0 && <span className="text-sm text-muted">{files.length} files: {files.map((f) => f.path).join(", ")}</span>}
        </div>
      </Field>
      {skipped.length > 0 && <p className="text-xs text-muted">Left out, because their names start with a dot: {skipped.join(", ")}.</p>}
      {files.length > 0 && (
        <Field label="Name" hint="Taken from the folder's name; agents load the skill by it.">
          <input className={`${inputCls} font-mono`} value={name} onChange={(e) => setName(typed(e.target.value))} />
        </Field>
      )}
      <PlaceFields agents={agents} place={place} onChange={setPlace} />
      <p className="text-xs text-muted">{NEVER}</p>
      <ErrorText error={error} />
      <div className="flex justify-end">
        <Button
          variant="primary"
          disabled={busy || !files.length || !NAME.test(name) || !placeReady(place)}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              onCreated(await post<SkillDetailDTO>("/api/skills", { name, ...placeBody(place), files }));
            } catch (err) {
              setError((err as Error).message);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Importing…" : "Import skill"}
        </Button>
      </div>
    </div>
  );
}
